"use client";

import { Box, Divider, Flex, useDisclosure } from "@chakra-ui/react";
import { Suspense, useCallback, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { MicrositeListingHero } from "@/components/shared/MicrositeListingHero";
import { PublicEntrance } from "@/components/shared/motion";
import { CustomSelect } from "@/components/shared/formElements/CustomSelect";
import { FiltersTriggerButton } from "@/components/ui";
import { useLocationsGet, usePublicListingPager, useTenantData } from "@/hooks";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { useThemeTokens } from "@/lib/theme/tokens";
import { usePrefetchPublicListingMediaUrls } from "@/features/media/hooks";
import { persistPublicListingHref } from "@/lib/utils/publicListingReturn";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { getActivePublicLocations } from "@/lib/utils/locationsPublic";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import {
  buildLocationsPageQuery,
  EMPTY_LOCATIONS_PAGE_URL_STATE,
  locationsPageParamsMatchSearchParams,
  LOCATIONS_PAGE_SIZE,
  type LocationsPageUrlState,
} from "./hooks/locationsPageUrl";
import { useLocationsFilter } from "./hooks/useLocationsFilter";
import { LocationsList } from "./LocationsList";
import {
  LocationsPageEmpty,
  LocationsPageError,
  LocationsPageNoResults,
} from "./LocationsPageError";
import { LocationsPageSkeleton } from "./LocationsPageSkeleton";
import {
  countLocationsPublicFilters,
  EMPTY_LOCATIONS_PUBLIC_FILTERS_DRAFT,
  LocationsPublicFiltersModal,
  type LocationsPublicFiltersDraft,
} from "./LocationsPublicFiltersModal";
import {
  PublicFilterChip,
  PublicFilterChipGroup,
  PublicFilterClearAll,
  PublicListingCount,
  PublicListingPagination,
  PublicListingResultsMeta,
  PublicListingSearchField,
  PublicListingToolbar,
  PublicListingUrlSync,
} from "@/views/shared/public-listing";

export const LocationsPageClient = ({
  hideHero = false,
  initialUrlState = EMPTY_LOCATIONS_PAGE_URL_STATE,
}: {
  hideHero?: boolean;
  initialUrlState?: LocationsPageUrlState;
}) => {
  const theme = useThemeTokens();
  const pathname = usePathname();
  const { tenantId, tenantData } = useTenantData();
  const studioName = getTenantDisplayName(tenantData);
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const {
    data,
    isPending,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useLocationsGet();

  const locations = useMemo(
    () => getActivePublicLocations(data?.items),
    [data?.items],
  );

  const {
    search,
    setSearch,
    cityFilter,
    setCityFilter,
    filteredData,
    hasActiveFilters,
    resetFilters,
    isSearchPending,
  } = useLocationsFilter(locations, {
    search: initialUrlState.search,
    cityFilter: initialUrlState.city,
  });

  const {
    isOpen: isFiltersModalOpen,
    onOpen: onFiltersModalOpen,
    onClose: onFiltersModalClose,
  } = useDisclosure();

  const {
    currentPage,
    totalPages,
    paginatedData,
    listTopRef,
    rangeStart,
    rangeEnd,
    resetToFirstPage,
    handlePrevious,
    handleNext,
  } = usePublicListingPager({
    items: filteredData,
    pageSize: LOCATIONS_PAGE_SIZE,
    initialPageZeroBased: Math.max(0, initialUrlState.page - 1),
    enabled: !isFiltersModalOpen,
  });

  const urlState = useMemo<LocationsPageUrlState>(
    () => ({
      search,
      city: cityFilter,
      page: currentPage + 1,
    }),
    [search, cityFilter, currentPage],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      resetToFirstPage();
    },
    [setSearch, resetToFirstPage],
  );

  const handleCityChange = useCallback(
    (value: string | null) => {
      setCityFilter(value);
      resetToFirstPage();
    },
    [setCityFilter, resetToFirstPage],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
    resetToFirstPage();
  }, [resetFilters, resetToFirstPage]);

  const [filtersDraft, setFiltersDraft] = useState<LocationsPublicFiltersDraft>(
    EMPTY_LOCATIONS_PUBLIC_FILTERS_DRAFT,
  );

  const appliedFiltersDraft = useMemo<LocationsPublicFiltersDraft>(
    () => ({ city: cityFilter }),
    [cityFilter],
  );

  const activeFilterCount = countLocationsPublicFilters(appliedFiltersDraft);
  const hasUnappliedFilters =
    isFiltersModalOpen && filtersDraft.city !== appliedFiltersDraft.city;

  const openFiltersModal = useCallback(() => {
    setFiltersDraft(appliedFiltersDraft);
    onFiltersModalOpen();
  }, [appliedFiltersDraft, onFiltersModalOpen]);

  const handleApplyFiltersDraft = useCallback(() => {
    setCityFilter(filtersDraft.city);
    resetToFirstPage();
  }, [filtersDraft.city, setCityFilter, resetToFirstPage]);

  const handleClearFiltersDraft = useCallback(() => {
    setFiltersDraft(EMPTY_LOCATIONS_PUBLIC_FILTERS_DRAFT);
  }, []);

  const persistListingHref = useCallback((href: string) => {
    persistPublicListingHref("locations", href);
  }, []);

  const listingHref = `${pathname}${buildLocationsPageQuery(urlState)}`;
  const isCurrentSearch = useCallback(
    (searchParams: URLSearchParams) =>
      locationsPageParamsMatchSearchParams(urlState, searchParams),
    [urlState],
  );

  const cityOptions = useMemo(() => {
    const cities = locations
      .map((location) => location.city?.trim())
      .filter((city): city is string => Boolean(city));
    return Array.from(new Set(cities)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [locations]);

  const showCityFilter = cityOptions.length > 1;

  const nextPageOwnerIds = useMemo(
    () =>
      filteredData
        .slice(rangeEnd, rangeEnd + LOCATIONS_PAGE_SIZE)
        .map((location) => Number(location.locationId))
        .filter((id) => id > 0),
    [filteredData, rangeEnd],
  );

  usePrefetchPublicListingMediaUrls({
    ownerType: EntryType.Location,
    ownerIds: nextPageOwnerIds,
    mediaGroup: MediaGroup.Cover,
  });

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if ((isLoading || isPending) && !data) {
    return <LocationsPageSkeleton hideHero={hideHero} />;
  }

  if (isError) {
    return <LocationsPageError onRetry={handleRetry} isRetrying={isFetching} />;
  }

  if (locations.length === 0) {
    return <LocationsPageEmpty />;
  }

  const tenantEmail = tenantData?.email ?? null;
  const hasSearchOrFilters = hasActiveFilters || search.trim().length > 0;

  const renderSearch = (describedById: string) => (
    <Box
      w={{ base: "100%", md: "auto" }}
      minW={{ base: 0, md: "320px" }}
      maxW={{ md: "420px" }}
      flexShrink={{ md: 0 }}
      overflow="hidden"
      ml={showCityFilter ? undefined : { md: "auto" }}
    >
      <PublicListingSearchField
        value={search}
        onChange={handleSearchChange}
        placeholder="Search by name, city, or address..."
        describedById={describedById}
        description={`Search through ${locations.length} locations`}
        isPending={isSearchPending}
      />
    </Box>
  );

  const filterChips = hasActiveFilters ? (
    <PublicFilterChipGroup>
      {cityFilter ? (
        <PublicFilterChip
          label={`City: ${cityFilter}`}
          removeLabel={`Remove city filter ${cityFilter}`}
          onRemove={() => handleCityChange(null)}
        />
      ) : null}
      <PublicFilterClearAll onClear={handleResetFilters} />
    </PublicFilterChipGroup>
  ) : null;

  return (
    <Flex
      direction="column"
      gap={{ base: 5, md: 6 }}
      maxW="1044px"
      mx="auto"
      w="100%"
    >
      <Suspense fallback={null}>
        <PublicListingUrlSync
          href={listingHref}
          persist={persistListingHref}
          isCurrentSearch={isCurrentSearch}
        />
      </Suspense>
      {!hideHero ? (
        <MicrositeListingHero
          subtitle={contentSettings.locationsSubtitle}
          title={contentSettings.locationsTitle}
          description={contentSettings.locationsDescription}
          studioName={studioName}
          tenantEmail={tenantEmail}
        />
      ) : null}

      {hideHero ? (
        <Divider borderColor={theme.borderGlass.subtle} opacity={0.7} />
      ) : null}

      <PublicEntrance variant="group" delay={0.1}>
        <PublicListingToolbar>
          {showCityFilter ? (
            <Flex
              display={{ base: "flex", md: "none" }}
              gap="10px"
              w="100%"
              minW={0}
              overflow="hidden"
              align="center"
            >
              <FiltersTriggerButton
                onClick={openFiltersModal}
                activeCount={activeFilterCount}
                hasUnapplied={hasUnappliedFilters}
                w="auto"
                flexShrink={0}
                data-testid="locations-public-filters-trigger"
              />
              <Box flex={1} minW={0} overflow="hidden">
                {renderSearch("locations-search-description-mobile")}
              </Box>
            </Flex>
          ) : (
            <Box display={{ base: "block", md: "none" }} w="100%" minW={0} overflow="hidden">
              {renderSearch("locations-search-description-mobile")}
            </Box>
          )}

          {showCityFilter ? (
            <Box
              display={{ base: "none", md: "block" }}
              flex={1}
              minW={{ md: "220px" }}
            >
              <CustomSelect
                placeholder="All Cities"
                formControlProps={{
                  w: { base: "100%", md: "max-content" },
                }}
                options={[
                  { label: "All Cities", value: "" },
                  ...cityOptions.map((city) => ({
                    label: city,
                    value: city,
                  })),
                ]}
                value={cityFilter ?? ""}
                onChange={(val) => handleCityChange(val === "" ? null : val)}
              />
            </Box>
          ) : null}

          <Box display={{ base: "none", md: "block" }}>
            {renderSearch("locations-search-description")}
          </Box>
        </PublicListingToolbar>

        {filteredData.length === 0 ? (
          <>
            <PublicListingResultsMeta chips={filterChips} />
            <LocationsPageNoResults hasActiveQuery={hasSearchOrFilters} />
          </>
        ) : (
          <>
            <PublicListingResultsMeta chips={filterChips}>
              <PublicListingCount
                count={filteredData.length}
                singular="location"
                plural="locations"
              />
            </PublicListingResultsMeta>
            <Box ref={listTopRef} position="relative">
              <LocationsList data={paginatedData} />
            </Box>
            <PublicListingPagination
              currentPageZeroBased={currentPage}
              totalPages={totalPages}
              totalCount={filteredData.length}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onPrev={handlePrevious}
              onNext={handleNext}
              ariaLabel="Location list pagination"
            />
          </>
        )}
      </PublicEntrance>

      {showCityFilter ? (
        <LocationsPublicFiltersModal
          isOpen={isFiltersModalOpen}
          onClose={onFiltersModalClose}
          draft={filtersDraft}
          onDraftChange={setFiltersDraft}
          onApply={handleApplyFiltersDraft}
          onClearDraft={handleClearFiltersDraft}
          cityOptions={cityOptions}
        />
      ) : null}
    </Flex>
  );
};
